import React, { useState, useEffect, useRef, useCallback } from 'react';
import { brand as brandConfig } from '../lib/branding';
import DOMPurify from 'dompurify';

type Platform = 'ios' | 'android' | 'desktop' | 'unknown';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

function detectPlatform(): Platform {
  try {
    if (typeof window === 'undefined') return 'unknown';
    const ua = navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    if (isIos) return 'ios';
    const isAndroid = /android/.test(ua);
    if (isAndroid) return 'android';
    return 'desktop';
  } catch {
    return 'unknown';
  }
}

/**
 * Smart PWA install prompt banner.
 *
 * - Slides up from just above the bottom nav
 * - Platform-specific CTA:
 *   - iOS Safari → shows step-by-step "Add to Home Screen" instructions
 *   - Android Chrome → triggers native `beforeinstallprompt`
 *   - Desktop → nothing (hidden)
 * - Dismiss stores localStorage flag for 7 days
 * - Hidden on standalone (already installed) and desktop
 * - Accessible: role="dialog", aria-label, keyboard dismissible
 */
export default function PwaInstallBanner() {
  const deferredPrompt = useRef<any>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [platform, setPlatform] = useState<Platform>(detectPlatform);
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      const stored = localStorage.getItem(DISMISS_KEY);
      return stored ? (Date.now() - parseInt(stored, 10)) < DISMISS_TTL : false;
    } catch { return false; }
  });
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [visible, setVisible] = useState(false);

  let isStandalone = false;
  try {
    isStandalone =
      typeof window !== 'undefined' &&
      (window.matchMedia('(display-mode: standalone)').matches ||
       (window.navigator as any).standalone === true);
  } catch {
    isStandalone = false;
  }

  useEffect(() => {
    if (!isStandalone && !isDismissed && platform !== 'desktop' && platform !== 'unknown') {
      setVisible(true);
    }
  }, [isStandalone, isDismissed, platform]);

  useEffect(() => {
    if (isStandalone) return;
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
      setPlatform(detectPlatform());
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone]);

  const triggerInstall = useCallback(async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      try {
        const result = await deferredPrompt.current.userChoice;
        deferredPrompt.current = null;
        setCanInstall(false);
        if (result.outcome === 'accepted') {
          try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
          setIsDismissed(true);
        }
      } catch {
        deferredPrompt.current = null;
        setCanInstall(false);
      }
    }
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setIsDismissed(true);
  }, []);

  if (isStandalone || isDismissed || !visible) return null;
  if (platform === 'desktop') return null;
  if (platform === 'unknown' && !canInstall) return null;

  const handleInstall = async () => {
    if (platform === 'ios') {
      setShowIosSteps(true);
      return;
    }
    await triggerInstall();
  };

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => { dismiss(); }, 300);
  };

  if (showIosSteps) {
    return (
      <div
        className="fixed z-[9000] left-4 right-4 flex flex-col md:hidden"
        style={{ bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 12px)' }}
        role="dialog"
        aria-label={`Install ${brandConfig.businessName} Portal instructions`}
      >
        <div className="bg-white rounded-[20px] shadow-xl border border-slate-200 p-5 animate-slide-up">
          <div className="flex items-center space-x-3 mb-4">
            <img src="/pwa-192x192.png" alt={brandConfig.businessName} className="w-10 h-10 rounded-xl shadow-sm" />
            <div>
              <h3 className="text-sm font-bold text-slate-800">{`Install ${brandConfig.businessName} Portal`}</h3>
              <p className="text-[11px] text-slate-500">Add to your Home Screen</p>
            </div>
          </div>
          <div className="space-y-3">
            <Step number={1} text='Tap the Share button' />
            <div className="flex justify-center py-2">
              <div className="relative flex items-center justify-center w-12 h-10 bg-blue-50 rounded-xl border border-blue-100">
                <svg className="w-6 h-6 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15v3a2 2 0 002 2h10a2 2 0 002-2v-3m-9-6l2-2m0 0l2 2m-2-2v10" />
                  <rect x="10" y="3" width="4" height="1" rx="0.5" />
                </svg>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 text-center -mt-1">
              <span className="font-semibold text-slate-500">Safari</span> — at the bottom of the screen
            </p>
            <Step number={2} text='Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>' />
            <Step number={3} text='Tap <strong>&ldquo;Add&rdquo;</strong> in the top right corner' />
          </div>
          <div className="flex justify-center mt-4 mb-1 animate-bounce" aria-hidden="true">
            <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
          <button
            onClick={handleDismiss}
            className="w-full mt-2 py-2.5 rounded-xl btn-primary  text-white text-xs font-bold transition-all cursor-pointer active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed z-[9000] left-4 right-4 flex flex-col md:hidden animate-slide-up"
      style={{ bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 12px)' }}
      role="dialog"
      aria-label={`Install ${brandConfig.businessName} Portal app`}
    >
      <div className="bg-white rounded-[20px] shadow-xl border border-slate-200 p-5">
        <div className="flex items-center space-x-3 mb-3">
          <img src="/pwa-192x192.png" alt={brandConfig.businessName} className="w-12 h-12 rounded-xl shadow-sm" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-800">{`Install ${brandConfig.businessName} Portal`}</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Get the full app experience</p>
          </div>
        </div>
        <button
          onClick={handleInstall}
          className="w-full py-2.5 rounded-xl btn-primary  text-white text-xs font-bold transition-all cursor-pointer active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
        >
          {platform === 'ios' ? 'Add to Home Screen' : 'Install App'}
        </button>
        <button
          onClick={handleDismiss}
          className="w-full mt-2 py-1.5 text-center text-[11px] text-gray-400 hover:text-gray-500 font-medium transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2 rounded-lg"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function Step({ number, text }: { number: number; text: string }) {
  return (
    <div className="flex items-start space-x-2.5">
      <span
        className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-brand text-[10px] font-bold shrink-0 mt-0.5"
        aria-hidden="true"
      >
        {number}
      </span>
      <p className="text-xs text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text, { ALLOWED_TAGS: ['strong', 'b', 'i', 'em', 'br'] }) }} />
    </div>
  );
}
